import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import PortfolioOverview from './components/PortfolioOverview';
import AssetTable from './components/AssetTable';
import PortfolioChart from './components/PortfolioChart';
import PortfolioAnalytics from './components/PortfolioAnalytics';
import TransactionHistory from './components/TransactionHistory';

const PortfolioManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sortBy, setSortBy] = useState('value');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [timeframe, setTimeframe] = useState('24h');

  // Mock portfolio data
  const portfolioData = {
    totalValue: 125847.32,
    totalChange24h: 3247.85,
    totalChangePercent24h: 2.65,
    totalChange7d: -1892.45,
    totalChangePercent7d: -1.48,
    totalChange30d: 8934.12,
    totalChangePercent30d: 7.64,
    lastUpdated: new Date()
  };

  const holdings = [
    {
      id: 'btc',
      symbol: 'BTC',
      name: 'Bitcoin',
      quantity: 2.45678,
      currentPrice: 43250.00,
      averageBuyPrice: 41200.00,
      currentValue: 106234.85,
      unrealizedPnL: 5034.85,
      unrealizedPnLPercent: 4.98,
      allocation: 84.4,
      change24h: 2.3,
      icon: 'Bitcoin'
    },
    {
      id: 'eth',
      symbol: 'ETH',
      name: 'Ethereum',
      quantity: 5.8923,
      currentPrice: 2650.00,
      averageBuyPrice: 2580.00,
      currentValue: 15614.60,
      unrealizedPnL: 412.61,
      unrealizedPnLPercent: 2.71,
      allocation: 12.4,
      change24h: 1.8,
      icon: 'Zap'
    },
    {
      id: 'ada',
      symbol: 'ADA',
      name: 'Cardano',
      quantity: 1250.00,
      currentPrice: 0.485,
      averageBuyPrice: 0.520,
      currentValue: 606.25,
      unrealizedPnL: -43.75,
      unrealizedPnLPercent: -6.73,
      allocation: 0.5,
      change24h: -3.2,
      icon: 'Triangle'
    },
    {
      id: 'sol',
      symbol: 'SOL',
      name: 'Solana',
      quantity: 45.2,
      currentPrice: 98.50,
      averageBuyPrice: 95.20,
      currentValue: 4452.20,
      unrealizedPnL: 149.16,
      unrealizedPnLPercent: 3.47,
      allocation: 3.5,
      change24h: 4.1,
      icon: 'Sun'
    },
    {
      id: 'dot',
      symbol: 'DOT',
      name: 'Polkadot',
      quantity: 125.8,
      currentPrice: 7.25,
      averageBuyPrice: 8.10,
      currentValue: 912.05,
      unrealizedPnL: -106.93,
      unrealizedPnLPercent: -10.49,
      allocation: 0.7,
      change24h: -2.8,
      icon: 'Circle'
    }
  ];

  const transactionHistory = [
    {
      id: 'tx1',
      type: 'buy',
      symbol: 'BTC',
      quantity: 0.5,
      price: 42800.00,
      total: 21400.00,
      fee: 21.40,
      timestamp: new Date(Date.now() - 86400000),
      status: 'completed'
    },
    {
      id: 'tx2',
      type: 'sell',
      symbol: 'ETH',
      quantity: 2.0,
      price: 2620.00,
      total: 5240.00,
      fee: 5.24,
      timestamp: new Date(Date.now() - 172800000),
      status: 'completed'
    },
    {
      id: 'tx3',
      type: 'buy',
      symbol: 'SOL',
      quantity: 25.0,
      price: 96.50,
      total: 2412.50,
      fee: 2.41,
      timestamp: new Date(Date.now() - 259200000),
      status: 'completed'
    }
  ];

  const performanceData = [
    { date: '2024-01-01', value: 118500 },
    { date: '2024-01-02', value: 119200 },
    { date: '2024-01-03', value: 121800 },
    { date: '2024-01-04', value: 120400 },
    { date: '2024-01-05', value: 122600 },
    { date: '2024-01-06', value: 124200 },
    { date: '2024-01-07', value: 125847 }
  ];

  const filteredHoldings = useMemo(() => {
    let filtered = holdings;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(holding => 
        holding.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        holding.symbol.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(holding => {
        switch (filterBy) {
          case 'profitable':
            return holding.unrealizedPnL > 0;
          case 'losing':
            return holding.unrealizedPnL < 0;
          case 'major':
            return holding.allocation > 5;
          case 'minor':
            return holding.allocation <= 5;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'value':
          aValue = a.currentValue;
          bValue = b.currentValue;
          break;
        case 'pnl':
          aValue = a.unrealizedPnL;
          bValue = b.unrealizedPnL;
          break;
        case 'allocation':
          aValue = a.allocation;
          bValue = b.allocation;
          break;
        case 'change':
          aValue = a.change24h;
          bValue = b.change24h;
          break;
        default:
          aValue = a.currentValue;
          bValue = b.currentValue;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [holdings, searchTerm, filterBy, sortBy, sortOrder]);

  const handleExportPDF = () => {
    // Mock PDF export functionality
    const exportData = {
      portfolioValue: portfolioData.totalValue,
      holdings: filteredHoldings,
      transactions: transactionHistory,
      exportDate: new Date().toISOString()
    };
    
    console.log('Exporting portfolio data:', exportData);
    // In a real app, this would generate and download a PDF
  };

  const handleQuickTrade = (symbol, action) => {
    navigate('/trading-dashboard', { 
      state: { 
        selectedPair: `${symbol}/USDT`,
        orderType: action === 'buy' ? 'market-buy' : 'market-sell'
      }
    });
  };

  const tabs = [
    { id: 'overview', label: 'Portfolio Overview', icon: 'PieChart' },
    { id: 'transactions', label: 'Transaction History', icon: 'History' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Assets' },
    { value: 'profitable', label: 'Profitable' },
    { value: 'losing', label: 'Losing' },
    { value: 'major', label: 'Major Holdings (>5%)' },
    { value: 'minor', label: 'Minor Holdings (≤5%)' }
  ];

  const sortOptions = [
    { value: 'value', label: 'Current Value' },
    { value: 'name', label: 'Asset Name' },
    { value: 'pnl', label: 'P&L' },
    { value: 'allocation', label: 'Allocation' },
    { value: 'change', label: '24h Change' }
  ];

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center space-x-2 mb-6">
          <button
            onClick={() => navigate('/trading-dashboard')}
            className="text-text-secondary hover:text-text-primary transition-colors duration-trading-fast"
          >
            <Icon name="Home" size={16} />
          </button>
          <Icon name="ChevronRight" size={16} className="text-text-secondary" />
          <span className="font-body-medium text-text-primary">Portfolio Management</span>
        </div>

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading-bold text-text-primary mb-2">
              Portfolio Management
            </h1>
            <p className="text-text-secondary font-body-normal">
              Monitor your holdings, track performance, and analyze your investment portfolio
            </p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <button
              onClick={handleExportPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-secondary-100 text-text-primary rounded-trading hover:bg-secondary-200 transition-colors duration-trading-fast"
            >
              <Icon name="Download" size={16} />
              <span className="font-body-medium text-sm">Export PDF</span>
            </button>
            
            <button
              onClick={() => navigate('/trading-dashboard')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
            >
              <Icon name="Plus" size={16} />
              <span className="font-body-medium text-sm">New Trade</span>
            </button>
          </div>
        </div>

        {/* Portfolio Overview Cards */}
        <PortfolioOverview 
          portfolioData={portfolioData}
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
        />

        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 mb-6 border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 font-body-medium text-sm border-b-2 transition-all duration-trading-fast ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-200'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3 space-y-6">
              {/* Portfolio Chart */}
              <PortfolioChart 
                data={performanceData}
                timeframe={timeframe}
                totalValue={portfolioData.totalValue}
                change24h={portfolioData.totalChange24h}
                changePercent24h={portfolioData.totalChangePercent24h}
              />

              {/* Controls */}
              <div className="bg-surface border border-border rounded-trading p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    {/* Search */}
                    <div className="relative">
                      <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
                      <input
                        type="text"
                        placeholder="Search assets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
                      />
                    </div>

                    {/* Filter */}
                    <select
                      value={filterBy}
                      onChange={(e) => setFilterBy(e.target.value)}
                      className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
                    >
                      {filterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Sort */}
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          Sort by {option.label}
                        </option>
                      ))}
                    </select>

                    {/* Sort Order */}
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="p-2 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
                    >
                      <Icon 
                        name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                        size={16} 
                        className="text-text-secondary" 
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Assets Table */}
              <AssetTable 
                holdings={filteredHoldings}
                onQuickTrade={handleQuickTrade}
              />
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <PortfolioAnalytics 
                holdings={holdings}
                totalValue={portfolioData.totalValue}
              />
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <TransactionHistory 
            transactions={transactionHistory}
          />
        )}
      </div>
    </div>
  );
};

export default PortfolioManagement;